import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { infrastructureParametersSchema } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { useEffect } from "react";
import { z } from "zod";
import { InfrastructureRequest } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/requestSchema";
import { PreviewResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { trainFormSchema } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainInfoRequest } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/requestSchema";

/**
 * A debounced function to fetch previews of the texts produced if the current form state were submitted as a new or
 * updated event.
 *
 * @param schema the zod schema to use for parsing the formState
 * @param formState the current state of the form, valid or invalid
 * @param urlPath the path to the query endpoint you want to use (depending on event type)
 * @param buildRequestBody a function that transforms a successfully parsed form into a request body for the endpoint
 * @returns an object with the following keys:
 * - `previewData` with example internal and external texts successfully fetched using a valid form, or undefined otherwise.
 * - `previewStatus` describing the current status of the mutation.
 */
export function useSinglePreview<
  Schema extends typeof infrastructureParametersSchema | typeof trainFormSchema,
  RequestBody extends Schema extends typeof infrastructureParametersSchema
    ? InfrastructureRequest
    : TrainInfoRequest,
>(
  schema: Schema,
  formState: z.infer<Schema>,
  urlPath:
    | "infrastructure-event/preview"
    | "trainInformation/singleTrainPreview",
  buildRequestBody: (
    formData: NonNullable<ReturnType<Schema["safeParse"]>["data"]>,
  ) => RequestBody,
) {
  const {
    mutate: preview,
    data: previewData,
    status: previewStatus,
    reset: resetPreview,
  } = useMutation({
    mutationFn: (body: RequestBody) =>
      mutationFnPOST<PreviewResponse, RequestBody>(
        `${getBackendUrl()}/${urlPath}`,
        body,
      ),
  });

  // "The watch result is optimized for the render phase rather than useEffect dependencies.
  // To detect value updates, you may want to use an external custom hook for value comparison."
  // – https://react-hook-form.com/docs/useform/watch
  // We don't have any deep equal libraries, so string comparison it is.
  const formString = JSON.stringify(formState);

  useEffect(() => {
    const timer = setTimeout(() => {
      const parsedForm = schema.safeParse(formState);
      if (parsedForm.success) {
        const body = buildRequestBody(parsedForm.data);
        if (body) {
          preview(body);
        }
      } else {
        resetPreview();
      }
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [formString]);

  return {
    previewData,
    previewStatus,
  };
}
